import EducationGradeRepository from '@api/repositories/education-grade-repository'
import { IEducationGrade } from '@app/interfaces/education/education-grade'
import FormList from '@app/pages/reference-book-page-details/components/form-list/form-list'
import ModalFooter from '@app/shared/modal-footer/modal-footer'
import { openNotificationWithIcon } from '@app/shared/notification/notification'
import {
  createEducationGrade,
  updateEducationGrade,
} from '@app/store/actions/eduction-grade-actions'
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  Switch,
} from 'antd'
import { AxiosResponse } from 'axios'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

interface props {
  educationGrade?: IEducationGrade
  edit?: boolean
}

const EducationGradeForm: React.FC<props> = ({ educationGrade, edit }) => {
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false)
  const [visible, setVisible] = useState<boolean>(false)
  const [form] = Form.useForm()
  const [t] = useTranslation()

  const showModal = () => setVisible(true)
  const hideModal = () => {
    setVisible(false)
    form.resetFields()
  }
  const dispatch = useDispatch()
  const educationGrades = [
    'TWO_THREE',
    'THREE_FOUR',
    'FOUR_FIVE',
    'FIVE_SIX',
    'SIX_PLUS',
    'LOT_AGE',
    'YEARLY_TRAINING',
    'SHORT_TERM',
    'SPECIALIZED',
    'MOBILE',
  ]
  const onFinish = async (values: any) => {
    const languages = values.data.filter(
      (language) => language && language.name,
    )
    const newValues: IEducationGrade = {
      id: values.id,
      sorting: values.sorting,
      status: values.status,
      data: languages,
      type: values.type,
    }
    setConfirmLoading(true)
    const employeeEducationSubmitApi = educationGrade
      ? EducationGradeRepository.update
      : EducationGradeRepository.create

    await employeeEducationSubmitApi(newValues)
      .then((response: AxiosResponse<IEducationGrade>) => {
        if (educationGrade && response.status === 200) {
          setConfirmLoading(false)
          dispatch(updateEducationGrade(response.data))
          form.resetFields()
          openNotificationWithIcon('success', t('success.updated'))
        }
        if (!educationGrade && response.status === 201) {
          setConfirmLoading(false)
          dispatch(createEducationGrade(response.data))
          form.resetFields()
          openNotificationWithIcon('success', t('success.created'))
        }
        hideModal()
      })
      .catch(() => {
        setConfirmLoading(false)
        openNotificationWithIcon('error', t('errors.error'))
      })
  }

  return (
    <React.Fragment>
      <Button onClick={showModal} type={'primary'}>
        {edit ? (
          <i className="far fa-edit"> {t('general.edit')} </i>
        ) : (
          t('general.add')
        )}
      </Button>

      <Modal
        open={visible}
        title={edit ? t('educationGrade.edit') : t('educationGrade.add')}
        confirmLoading={confirmLoading}
        onCancel={hideModal}
        footer={[
          <React.Fragment key={1}>
            <ModalFooter
              confirm={confirmLoading}
              edit={edit}
              onCancel={hideModal}
              onSubmit={form.submit}
            />
          </React.Fragment>,
        ]}
      >
        <Spin spinning={confirmLoading}>
          <Form
            form={form}
            onFinish={onFinish}
            initialValues={{
              id: educationGrade?.id,
              sorting: educationGrade?.sorting || 0,
              status: edit ? educationGrade?.status : true,
              type: educationGrade?.type,
            }}
            layout={'vertical'}
          >
            <Form.Item hidden={true} name={'id'}>
              <Input />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('errors.requiredMessage'),
                },
              ]}
              name={'status'}
              label={t('general.status')}
              valuePropName="checked"
            >
              <Switch
                disabled={confirmLoading}
                defaultChecked={educationGrade?.status === true}
              />
            </Form.Item>
            <Form.Item
              rules={[
                {
                  required: true,
                  type: 'number',
                  min: -2147483648,
                  max: 2147483648,
                  message: t('errors.minPriorityMessage'),
                },
              ]}
              label={t('general.sort')}
              name={'sorting'}
            >
              <InputNumber
                disabled={confirmLoading}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label={t('educationGrade.types.type')} name={'type'}>
              <Select disabled={confirmLoading}>
                {educationGrades.map((institutionStatusTypes, index) => {
                  return (
                    <Select.Option key={index} value={institutionStatusTypes}>
                      {t(`educationGrade.types.${institutionStatusTypes}`)}
                    </Select.Option>
                  )
                })}
              </Select>
            </Form.Item>
            <FormList confirmLoading={confirmLoading} object={educationGrade} />
          </Form>
        </Spin>
      </Modal>
    </React.Fragment>
  )
}

export default EducationGradeForm
